import { ICoreContext, getAsset } from "@msdyn365-commerce/core";
import { SimpleProduct } from "@msdyn365-commerce/retail-proxy";

const currencyFormatterDecimals = new Intl.NumberFormat('nb-NO', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
});
const currencyFormatterNoDecimals = new Intl.NumberFormat('nb-NO', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
});

/**
 * Gets the default quantity to use when buying a product.
 * @param product The product to get the default quantity for.
 * @param breakoff Whether breakoff is enabled.
 * @param defaultQuantity The default quantity to use if no other quantity is found. Defaults to 1.
 * @returns The default quantity to use for the product.
 */
export function getDefaultBuyQuantity(product: SimpleProduct, breakoff: boolean, defaultQuantity: number = 1): number {
    if (breakoff) {
        return 1 || defaultQuantity;
    }

    return product.Behavior?.MultipleOfQuantity || defaultQuantity;
}

export function formatCurrencyNOK(price: number | undefined, context?: ICoreContext, currencyCode: string = 'NOK', noDecimals?: boolean): string {
    if (price === undefined) {
        return '';
    }

    if (!context || currencyCode === 'NOK') {
        if (noDecimals) {
            return `${currencyFormatterNoDecimals.format(Math.trunc(price))} kr`;
        }

        return `${currencyFormatterDecimals.format(price)} kr`;
    } else {
        return context.cultureFormatter.formatCurrency(price, currencyCode);
    }
}

const currencyFormatters: { [currencyCode: string]: Intl.NumberFormat } = {};

function ensureCurrencyFormatter(currencyCode: string | undefined, locale: string | undefined): Intl.NumberFormat {
    if (!currencyFormatters[currencyCode || '']) {
        currencyFormatters[currencyCode || ''] = new Intl.NumberFormat(locale, {
            style: currencyCode ? 'currency' : 'decimal',
            currency: currencyCode,
            currencyDisplay: 'symbol'
        });
    }
    return currencyFormatters[currencyCode || ''];
}

export function formatCurrencyToParts(price: number | undefined, context: ICoreContext, currencyCode: string | undefined = 'NOK'): Intl.NumberFormatPart[] {
    const currencyFormatter = ensureCurrencyFormatter(currencyCode, context.request.locale);
    const parts = currencyFormatter.formatToParts(price || 0);

    if (context.request.locale === 'nb-NO' && currencyCode === 'NOK') {
        // Make sure symbol is after the number for Norwegian locale and NOK currency
        const symbolIndex = parts.findIndex(part => part.type === 'currency');
        const integerIndex = parts.findIndex(part => part.type === 'integer');
        if (symbolIndex < integerIndex) {
            const symbolPart = parts.splice(symbolIndex, 1)[0];
            parts.splice(parts.length, 0, { type: 'literal', value: ' ' }, symbolPart);

            if (parts[0].type === 'literal' && parts[0].value.trim() === '') {
                parts.shift(); // Remove leading space if it exists
            }
        }
    }

    return parts;
}

/**
 * Returns a new array with unique items based on a specific property.
 * @param array The array to filter.
 * @param property The property to check for uniqueness.
 * @returns A new array with unique items.
 */
export function arrayUniqueByProperty<T>(array: T[], property: keyof T): T[] {
    return array.filter((x, i) => array.findIndex(y => y[property] === x[property]) === i);
}

export function getAssetFixed(context: ICoreContext, assetPath: string): string {
    let assetUrl = getAsset(assetPath, context.request);

    if (typeof window === "undefined") {
        return assetUrl;
    }

    const SUBMISSIONID = (window as any | undefined)?.___initialData___?._environmentVariable?.['SUBMISSIONID'];

    if (assetUrl.includes('00000-00000-00000-00000-00000') && SUBMISSIONID) {
        assetUrl = assetUrl.replace('00000-00000-00000-00000-00000', SUBMISSIONID);
    }

    return assetUrl;
}

export function cssCapitalize(str: string | undefined, locale: string | undefined = undefined): string | undefined {
    if (str === undefined) {
        return undefined;
    }
    return str.toLocaleLowerCase(locale).replace(
        /(^|[\s!"#$%&'()*+,\-./:;<=>?@[\\\]^`{|}~])(\p{L})/gu,
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        (_, prefix, char) => prefix + char.toLocaleUpperCase(locale)
    );
}
